<template>
  <b-modal
    modal-class="component-modal-product"
    id="transferOutItemModal"
    ref="transferOutItemModal"
    size="xl"
    :title="modalTitle"
    no-close-on-backdrop
    @close="close"
  >
    <b-form id="transferOutTable" @submit.prevent="saveTransferOut">
      <b-form-group label-cols="3" label="เหตุผล:" label-align="right">
        <b-form-textarea
          ref="reasonTransferOutInput"
          class="required no-scrollbar"
          id="reasonTransferOut"
          v-model="reasonTransferOut"
          maxlength="500"
          rows="2"
          max-rows="7"
          no-resize
          placeholder="กรอกรายละเอียด"
          :class="{
            'is-invalid': $v.reasonTransferOut.$error,
          }"
        ></b-form-textarea>
      </b-form-group>

      <b-form-group label-cols="3" label="สาขาปลายทาง:" label-align="right">
        <div class="multiselect-wrapper">
          <multiselect
            class="col p-0 required"
            :class="{
              'is-invalid': $v.destinationBranchTransferOut.$error,
            }"
            v-model="destinationBranchTransferOut"
            :options="destinationBranchOptions"
            :multiple="false"
            placeholder="เลือกสาขาสำหรับรับสินค้า"
            track-by="id"
            :custom-label="customLabel"
            selectLabel="เลือกสาขา"
            selectedLabel="เลือกแล้ว"
            deselectLabel="คลิกเพื่อลบออก"
            openDirection="below"
          >
            <span slot="noResult">ไม่พบรายการ แนะนำเปลี่ยนคำค้นหา</span>
          </multiselect>
          <template v-if="$v.destinationBranchTransferOut.$error">
            <div class="invalid-icon-position">
              <i class="icon-invalid-exclamation"></i>
            </div>
          </template>
        </div>
      </b-form-group>

      <b-table
        id="TransferOutTable"
        ref="TransferOutTable"
        hover
        small
        sort-icon-left
        head-variant="light"
        :items="productAdjustmentItems"
        :fields="fields"
      >
        <template v-slot:cell(index)="row">
          <div class="d-flex align-items-center justify-content-center">
            {{ row.index + 1 }}.
          </div>
        </template>

        <template v-slot:cell(productNoAndName)="row">
          <div class="multiselect-wrapper">
            <multiselect
              class="col p-0 required"
              v-model="row.item.productNoAndName"
              :options="getProductOptions(row.index)"
              :multiple="false"
              placeholder="เลือกสินค้า"
              @input="onProductNoAndNameChange(row)"
              track-by="id"
              :custom-label="item => item.name || ''"
              selectLabel="เลือกสินค้า"
              selectedLabel="เลือกแล้ว"
              deselectLabel="คลิกเพื่อลบออก"
              openDirection="below"
              :class="{
                'is-invalid':
                  $v.productAdjustmentItems.$each[row.index] &&
                  $v.productAdjustmentItems.$each[row.index].productNoAndName
                    .$error,
              }"
            >
              <span slot="noResult">ไม่พบรายการ แนะนำเปลี่ยนคำค้นหา</span>
            </multiselect>
            <template
              v-if="
                $v.productAdjustmentItems.$each[row.index] &&
                $v.productAdjustmentItems.$each[row.index].productNoAndName
                  .$error
              "
            >
              <div class="invalid-icon-position">
                <i class="icon-invalid-exclamation"></i>
              </div>
            </template>
          </div>
        </template>

        <template v-slot:cell(adjustmentAmount)="row">
          <InputNumberWithButtonField
            :adjustStock="row.item.adjustmentAmount"
            :onChange="value => onChangeAdjustStock(row, value)"
            :disabled="!row.item.productNoAndName"
            :invalid="
              submitAttempted &&
              $v.productAdjustmentItems.$each[row.index] &&
              $v.productAdjustmentItems.$each[row.index].adjustmentAmount.$error
            "
            :max="0"
          />
        </template>

        <template v-slot:cell(currentStockToAfterAdjustment)="row">
          <PreAndPostStockField
            :currentStock="convertUnitStock(row)"
            :adjustStock="row.item.adjustmentAmount"
            :onChange="value => onChangeAdjustStock(row, value)"
            :disabled="!row.item.productNoAndName"
            :max="Number(row.item.productAvailableStock || 0)"
            :invalid="
              submitAttempted && isCurrentStockToAfterAdjustmentInvalid(row)
            "
          />
        </template>

        <template v-slot:cell(productUnit)="row">
          <multiselect
            class="col p-0 required"
            v-model="row.item.productUnit"
            :options="buildUomOptions(row.item.productNoAndName)"
            :multiple="false"
            :disabled="!row.item.productNoAndName"
            @input="onProductUnitChange(row)"
            placeholder="เลือกหน่วย"
            track-by="id"
            :custom-label="customLabel"
            selectLabel="เลือกหน่วยสินค้า"
            selectedLabel="เลือกแล้ว"
            :allow-empty="false"
            :clear-on-select="false"
            :close-on-select="true"
            openDirection="below"
            deselectLabel=""
            :class="{
              'is-invalid':
                $v.productAdjustmentItems.$each[row.index] &&
                $v.productAdjustmentItems.$each[row.index].productUnit.$error,
            }"
          >
            <span slot="noResult">ไม่พบรายการ แนะนำเปลี่ยนคำค้นหา</span>
          </multiselect>
        </template>

        <template v-slot:cell(buttons)="row">
          <button
            :disabled="row.index === 0 && productAdjustmentItems.length === 1"
            @click="deleteAdjustmentItem(row.item, row.index)"
            class="delete-btn"
          >
            <i class="fas fa-trash-alt"></i>
          </button>
        </template>

        <template v-slot:bottom-row>
          <td colspan="6" class="bottom-row-cell">
            <b-button
              type="button"
              variant="outline-primary"
              @click="addAdjustmentItem"
              class="bottom-add-row"
            >
              <i class="fas fa-plus"></i> เพิ่มรายการสินค้า
            </b-button>
          </td>
        </template>
      </b-table>
    </b-form>

    <template v-slot:modal-footer>
      <b-button type="submit" form="transferOutTable" variant="primary">
        สร้างรายการ
      </b-button>
    </template>
    <Dialog ref="Dialog"></Dialog>
    <Loading v-if="isLoading"></Loading>
  </b-modal>
</template>

<script>
import Dialog from "@/components/modal/Dialog";
import Loading from "@/components/Loading";
import "@/theme/modal/ProductAndStock/Product.scss";
import { maxLength, required, requiredIf } from "vuelidate/lib/validators";
import InputNumberWithButtonField from "../../../table/stock/fields/InputNumberWithButtonField.vue";
import PreAndPostStockField from "../../../table/stock/fields/PreAndPostStockField.vue";
import { mapActions, mapGetters } from "vuex";
import { Status } from "@/constants/productStock";

export default {
  name: "TransferOutCreateModal",
  components: {
    Dialog,
    Loading,
    InputNumberWithButtonField,
    PreAndPostStockField,
  },
  data() {
    return {
      isLoading: false,
      Status,
      submitAttempted: false,
      modalTitle: "เพิ่มรายการใบโอนสินค้า",
      reasonTransferOut: "",
      productList: [],
      conversionUnitRate: [],
      destinationBranchList: [],
      destinationBranchTransferOut: "",
      productAdjustmentItems: [
        {
          productNoAndName: "",
          adjustmentAmount: 0,
          productUnit: null,
        },
      ],
      fields: [
        {
          key: "index",
          label: "",
          thStyle: { width: "3%" },
          tdStyle: { width: "3%" },
        },
        {
          key: "productNoAndName",
          label: "รหัสสินค้า - ชื่อสินค้า",
          thStyle: { width: "35%" },
          tdStyle: { width: "35%" },
        },
        {
          key: "adjustmentAmount",
          label: "จำนวนที่โอนออก",
          thStyle: { width: "17%" },
          tdStyle: { width: "17%" },
        },
        {
          key: "currentStockToAfterAdjustment",
          label: "สต๊อก (ปัจจุบัน→หลังโอนออก)",
          thStyle: { width: "21%" },
          tdStyle: { width: "21%" },
        },
        {
          key: "productUnit",
          label: "หน่วยสินค้า",
          thStyle: { width: "18%" },
          tdStyle: { width: "18%" },
          tdClass: "text-wrap-cell",
        },
        {
          key: "buttons",
          label: "",
          thStyle: { width: "6%" },
          tdStyle: { width: "6%" },
        },
      ],
    };
  },
  validations: {
    reasonTransferOut: {
      required,
      maxLength: maxLength(500),
    },
    destinationBranchTransferOut: {
      required,
    },
    productAdjustmentItems: {
      $each: {
        productNoAndName: {
          required(value) {
            return !!(value && value.id);
          },
        },
        adjustmentAmount: {
          required(value, row) {
            if (!row || !row.productNoAndName || !row.productNoAndName.id)
              return true;
            return value != null && Number(value) !== 0;
          },
        },
        currentStockToAfterAdjustment: {
          required(value, row) {
            if (!row || !row.productNoAndName || !row.productNoAndName.id)
              return true;

            const after = Number(value);
            const current = Number(row.productAvailableStock) || 0;
            return after !== current;
          },
        },
        productUnit: {
          required: requiredIf(function (row) {
            return !!(row && row.productNoAndName && row.productNoAndName.id);
          }),
        },
      },
    },
  },
  computed: {
    productOptions() {
      return this.productList
        .filter(
          product =>
            product.productStatus === Status.LOCKED ||
            product.productStatus === Status.ACTIVE
        )
        .map(product => ({
          id: product.productId,
          name: `${product.productCode} - ${product.productNameMain}`,
        }));
    },
    destinationBranchOptions() {
      return this.destinationBranchList.map(destinationBranch => ({
        id: destinationBranch.branchId,
        name: destinationBranch.branchName,
      }));
    },
    ...mapGetters({
      getTransferList: "moduleTransferInventory/getTransferList",
    }),
  },
  methods: {
    ...mapActions({
      fetchTransferList: "moduleTransferInventory/fetchTransferList",
    }),
    onChangeAdjustStock(row, value) {
      const num = Number(value);

      this.$set(row.item, "adjustmentAmount", num);
      const current = Number(row.item.productAvailableStock) || 0;

      this.$set(row.item, "currentStockToAfterAdjustment", current + num);

      const rowV = this.$v.productAdjustmentItems?.$each?.[row.index];
      if (rowV?.adjustmentAmount?.$reset) rowV.adjustmentAmount.$reset();
      if (rowV?.currentStockToAfterAdjustment?.$reset)
        rowV.currentStockToAfterAdjustment.$reset();
    },
    show(data) {
      const { productList, destinationBranchList, branchInfo } = data;
      this.productList = productList;
      this.destinationBranchList = destinationBranchList;
      this.branchInfo = branchInfo.id;

      this.$bvModal.show("transferOutItemModal");
    },
    close() {
      this.$v.$reset();
      this.setDefaultValue();
      this.$bvModal.hide("transferOutItemModal");
    },
    setDefaultValue() {
      this.submitAttempted = false;
      this.reasonTransferOut = "";
      this.destinationBranchTransferOut = "";
      this.conversionUnitRate = [];
      this.productAdjustmentItems = [
        {
          productNoAndName: "",
          adjustmentAmount: 0,
          productUnit: null,
        },
      ];
    },
    saveTransferOut() {
      this.submitAttempted = true;
      this.$v.$touch();

      const reasonTransferOutInvalid = this.$v.reasonTransferOut.$invalid;
      const destinationBranchTransferOutInvalid =
        this.$v.destinationBranchTransferOut.$invalid;
      const productAdjustmentItemsInvalid =
        this.$v.productAdjustmentItems.$invalid;

      if (
        reasonTransferOutInvalid ||
        destinationBranchTransferOutInvalid ||
        productAdjustmentItemsInvalid
      ) {
        return;
      }

      const adjustTransferOutData = {
        reason: this.reasonTransferOut,
        fromBranchId: this.branchInfo,
        toBranchId: this.destinationBranchTransferOut.id,
        transferProducts: this.productAdjustmentItems.map(item => ({
          productId: item.productNoAndName.id,
          qtyUom: Math.abs(item.adjustmentAmount),
          uomId: item.productUnit.id,
        })),
      };

      this.$store
        .dispatch("moduleTransferInventory/createTransferOut", {
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
          adjustTransferOutData,
        })
        .then(res => {
          if (res.status == 201) {
            this.reloadTransferList();
            this.$parent.showDialogToast(
              "success",
              "เพิ่มรายการใบโอนสินค้าสำเร็จ"
            );
          }
        })
        .catch(err => {
          const errorMessage = err.response?.data?.errorMessage;

          const errorMsg =
            typeof errorMessage === "string"
              ? errorMessage
              : "สร้างรายการใบโอนสินค้าไม่สำเร็จ กรุณาลองใหม่";

          this.$parent.showDialogToast("danger", errorMsg);
        })
        .finally(() => {
          this.close();
          this.isLoading = false;
        });
    },
    reloadTransferList() {
      this.fetchTransferList({
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
      });
    },
    customLabel(item) {
      return item?.name || "";
    },
    buildUomOptions(productNoAndName) {
      const uoms = [];

      if (!productNoAndName) {
        return [];
      }

      const product = this.productList.find(
        p => p.productId === productNoAndName.id
      );

      if (!product) return uoms;

      if (product.productUnit) {
        uoms.push({
          id: product.productUnit.id,
          name: product.productUnit.description,
        });
      }

      if (product.productConversionUnitItems?.length) {
        product.productConversionUnitItems.forEach(item => {
          uoms.push({
            id: item.uom.id,
            name: item.uom.description,
          });
        });
      }

      const unique = uoms.filter(
        (uom, index, self) => index === self.findIndex(t => t.id === uom.id)
      );

      return unique;
    },
    onProductNoAndNameChange(row) {
      row.item.productUnit = null;

      if (row.item.productNoAndName?.id) {
        const product = this.productList.find(
          p => p.productId === row.item.productNoAndName.id
        );

        row.item.productUnit = product.productUnit
          ? {
              id: product.productUnit.id,
              name: product.productUnit.description,
            }
          : null;

        this.conversionUnitRate.push({
          id: product.productUnit.id,
          rate: 1,
        });

        if (product.productConversionUnitItems?.length) {
          product.productConversionUnitItems.forEach(item => {
            this.conversionUnitRate.push({
              id: item.uom.id,
              rate: 1 / item.qtyPerUnitOfMeasure,
            });
          });
        }

        const available = product?.productAvailableStock ?? 0;
        row.item.productAvailableStock = available;
        this.$set(row.item, "adjustmentAmount", 0);
        this.$set(row.item, "currentStockToAfterAdjustment", available);
      } else {
        row.item.productAvailableStock = undefined;

        this.$set(row.item, "adjustmentAmount", 0);
        row.item.currentStockToAfterAdjustment = undefined;
      }

      const rowValidation = this.$v.productAdjustmentItems?.$each?.[row.index];
      if (rowValidation?.productUnit?.$reset) {
        rowValidation.productUnit.$reset();
      }
      if (rowValidation?.adjustmentAmount?.$reset) {
        rowValidation.adjustmentAmount.$reset();
      }
      if (rowValidation?.currentStockToAfterAdjustment?.$reset) {
        rowValidation.currentStockToAfterAdjustment.$reset();
      }
    },
    onProductUnitChange(row) {
      if (row.item.productUnit?.id) {
        const product = this.productList.find(
          p => p.productId === row.item.productNoAndName.id
        );

        const available = product?.productAvailableStock ?? 0;

        const conversionRate = this.conversionUnitRate.find(
          r => r.id === row.item.productUnit.id
        );

        if (conversionRate) {
          const availableAfterConversion = available * conversionRate.rate;
          row.item.productAvailableStock = availableAfterConversion;
          this.$set(row.item, "adjustmentAmount", 0);
          this.$set(
            row.item,
            "currentStockToAfterAdjustment",
            availableAfterConversion
          );
        }
      } else {
        row.item.productAvailableStock = undefined;
        this.$set(row.item, "adjustmentAmount", 0);
        row.item.currentStockToAfterAdjustment = undefined;
      }
    },
    getProductOptions(currentIndex) {
      const selectedInOtherRows = new Set(
        this.productAdjustmentItems
          .map((item, i) =>
            i !== currentIndex && item.productNoAndName?.id
              ? item.productNoAndName.id
              : null
          )
          .filter(Boolean)
      );
      return this.productOptions.filter(
        product => !selectedInOtherRows.has(product.id)
      );
    },
    convertUnitStock(row) {
      return row.item.productAvailableStock;
    },
    isCurrentStockToAfterAdjustmentInvalid(row) {
      const rowV = this.$v.productAdjustmentItems?.$each?.[row.index];
      return !!(
        rowV &&
        rowV.currentStockToAfterAdjustment &&
        rowV.currentStockToAfterAdjustment.$error
      );
    },
    createEmptyAdjustmentItem() {
      return {
        productNoAndName: "",
        adjustmentAmount: 0,
        productUnit: null,
        currentStockToAfterAdjustment: null,
      };
    },
    addAdjustmentItem() {
      this.productAdjustmentItems.push(this.createEmptyAdjustmentItem());
    },
    deleteAdjustmentItem(item, index) {
      this.productAdjustmentItems.splice(index, 1);
    },
    formatProductStock(value) {
      if (value === null || value === "") return "";
      const num = Number(value);
      if (Number.isNaN(num)) return "";
      return num.toLocaleString("en-US");
    },
  },
  mounted() {
    this.$nextTick(() => {
      const el = this.$refs.reasonTransferOutInput;
      if (el) {
        el.style.height = "38px";
        el.style.height = `${el.scrollHeight}px`;
      }
    });
  },
};
</script>

<style scoped>
::v-deep .align-label .col-form-label {
  padding-top: 0px !important;
}

::v-deep .multiselect__tags {
  background-color: #ccecff6c;
  box-shadow: none;
}

::v-deep .multiselect__single,
::v-deep .multiselect__input,
::v-deep .multiselect__placeholder {
  background-color: transparent !important;
}

::v-deep span.multiselect__placeholder {
  margin-left: 6px !important;
}

::v-deep .multiselect__input,
::v-deep .multiselect__single {
  padding-left: 6px;
}

::v-deep span.multiselect__single {
  margin-left: 6px !important;
}

::v-deep .multiselect__single {
  margin-bottom: 0;
  padding-left: 0;
}

::v-deep .multiselect__tags {
  box-shadow: none;
}

::v-deep .multiselect--disabled {
  background-color: #ebebeb !important;
  color: #c6c6c6 !important;
  opacity: 1 !important;
}

::v-deep .multiselect--disabled .multiselect__tags {
  background-color: #ebebeb !important;
  cursor: not-allowed;
  overflow: hidden;
  color: #c6c6c6 !important;
}

::v-deep .multiselect--disabled .multiselect__placeholder {
  background-color: #ebebeb !important;
  color: #c6c6c6 !important;
}

::v-deep .multiselect--disabled .multiselect__select {
  background-color: #ebebeb !important;
  cursor: not-allowed;
  border: 1px solid #ced4da !important;
  border-left: none !important;
  overflow: hidden;

  border-radius: 4px;
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
}

::v-deep #TransferOutTable tbody tr td {
  border-top: none !important;
  border-bottom: 1px solid #dee2e6 !important;
}

::v-deep #transferOutItemModal .modal-body {
  padding-bottom: 0 !important;
}

::v-deep #TransferOutTable tbody tr:last-child td {
  border-bottom: none !important;
}

::v-deep .bottom-add-row {
  text-align: left !important;
  margin-top: 5px;
}

::v-deep #TransferOutTable.table-hover tbody tr:last-child:hover {
  background-color: transparent !important;
}

::v-deep #TransferOutTable thead tr th:nth-child(1),
::v-deep #TransferOutTable tbody tr:not(:last-child) td:nth-child(1) {
  text-align: center !important;
  vertical-align: middle !important;
}

::v-deep #TransferOutTable .bottom-row-cell {
  text-align: left !important;
}

::v-deep .btn-delete-adjustment:disabled {
  border-color: #dee2e6 !important;
  color: #dee2e6 !important;
}

::v-deep #TransferOutTable td.text-wrap-cell {
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}

::v-deep #TransferOutTable thead tr th,
::v-deep #TransferOutTable tbody tr td {
  vertical-align: middle !important;
}

::v-deep .delete-btn {
  background-color: transparent;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  color: #a3a4a6;
  font-size: 20px;
  width: 48px;
  height: 38px;
}

.delete-btn:hover:not(:disabled) {
  background-color: #d33;
  color: #fff;
}

.delete-btn:disabled {
  border-color: #ebebeb !important;
  color: #ebebeb !important;
  cursor: not-allowed;
}
</style>
